import { NavLink, Outlet } from "react-router-dom";
import { useAppState } from "../state/AppStateContext";
import { useTheme } from "../state/theme";
import { UiProvider, useUi } from "../state/ui";
import CommandPalette from "./CommandPalette";
import UploadDialog from "../features/upload/UploadDialog";

const IS_MAC =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
    isActive
      ? "bg-[var(--oe-accent-soft)] text-[var(--oe-accent)]"
      : "text-[var(--oe-text-muted)] hover:bg-[var(--oe-hover)] hover:text-[var(--oe-text)]"
  }`;

export default function Layout() {
  return (
    <UiProvider>
      <LayoutInner />
    </UiProvider>
  );
}

function LayoutInner() {
  const { isLoading, isRebuilding, loadError } = useAppState();
  const { theme, toggleTheme } = useTheme();
  const { openPalette, openUpload } = useUi();

  return (
    <div className="flex h-full min-h-screen flex-col bg-[var(--oe-bg)] text-[var(--oe-text)]">
      <header className="relative z-30 flex items-center justify-between gap-3 border-b border-[var(--oe-border)] bg-[var(--oe-surface)] px-4 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <NavLink to="/explore/classes" className="flex items-center gap-2.5">
            <BrandMark />
            <span className="whitespace-nowrap text-[15px] font-semibold tracking-tight">
              Ontology Explorer
            </span>
          </NavLink>
          <nav className="ml-2 flex items-center gap-1">
            <NavLink to="/explore/classes" className={navLinkClass}>
              Documentation
            </NavLink>
            <NavLink to="/visualize" className={navLinkClass}>
              Graph
            </NavLink>
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openPalette}
            className="flex w-56 items-center gap-2 rounded-lg border border-[var(--oe-border)] bg-[var(--oe-bg)] px-3 py-1.5 text-sm text-[var(--oe-text-muted)] transition-colors hover:border-[var(--oe-border-strong)]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <span className="flex-1 text-left">Search…</span>
            <kbd className="rounded border border-[var(--oe-border)] bg-[var(--oe-surface)] px-1.5 py-0.5 font-sans text-[10px] font-medium">
              {IS_MAC ? "⌘K" : "Ctrl K"}
            </kbd>
          </button>

          <button
            type="button"
            onClick={openUpload}
            className="flex items-center gap-1.5 rounded-lg border border-[var(--oe-border)] px-3 py-1.5 text-sm font-medium text-[var(--oe-text)] transition-colors hover:border-[var(--oe-border-strong)] hover:bg-[var(--oe-hover)]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 16V4m0 0 4 4m-4-4L8 8" />
              <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
            </svg>
            Upload
          </button>

          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-lg border border-[var(--oe-border)] p-2 text-[var(--oe-text-muted)] transition-colors hover:border-[var(--oe-border-strong)] hover:text-[var(--oe-text)]"
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          >
            {theme === "dark" ? <SunIcon /> : <MoonIcon />}
          </button>
        </div>
      </header>

      {isRebuilding && !isLoading && (
        <div className="oe-progress h-0.5 shrink-0 bg-[var(--oe-surface-raised)]" aria-hidden="true" />
      )}

      <main className="flex min-h-0 flex-1 flex-col">
        {isLoading ? (
          <LoadingState />
        ) : loadError ? (
          <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-[var(--oe-danger)]">
            {loadError}
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      <CommandPalette />
      <UploadDialog />
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3">
      <div className="oe-progress h-1 w-48 rounded-full bg-[var(--oe-surface-raised)]" />
      <p className="text-sm text-[var(--oe-text-muted)]">Loading ontologies…</p>
    </div>
  );
}

function BrandMark() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="5" r="2.6" fill="var(--oe-accent)" />
      <circle cx="5" cy="17" r="2.6" fill="var(--oe-accent)" opacity="0.55" />
      <circle cx="19" cy="17" r="2.6" fill="var(--oe-accent)" opacity="0.55" />
      <path
        d="M12 7.5 6.2 15M12 7.5l5.8 7.5M7.5 17h9"
        stroke="var(--oe-accent)"
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.7"
      />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  );
}
